import React, { useEffect, useState, useMemo } from 'react';
import { 
  Box, 
  Table, 
  TableBody, 
  TableCell, 
  TableContainer, 
  TableHead, 
  TableRow, 
  Paper, 
  Button, 
  Dialog, 
  DialogTitle, 
  DialogContent, 
  Chip,
  Typography,
  Alert,
  IconButton,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TablePagination,
  TableSortLabel,
  SelectChangeEvent
} from '@mui/material';
import PageHeader from '../../components/layout/PageHeader';
import { Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon, Search as SearchIcon, Clear as ClearIcon } from '@mui/icons-material';
import { 
  getHomeExpenseGroups, 
  createHomeExpenseGroup, 
  updateHomeExpenseGroup, 
  deleteHomeExpenseGroup, 
  getHomeExpenseGroupById 
} from '../../services/homeExpensesApi';
import { HomeExpenseGroupDto, ExpenseType } from '../../types/homeExpenses';
import HomeExpenseGroupForm from './HomeExpenseGroupForm';

interface HomeExpenseGroupListProps {
  expenseType?: ExpenseType;
}

const HomeExpenseGroupList: React.FC<HomeExpenseGroupListProps> = ({ expenseType }) => {
  const [groups, setGroups] = useState<HomeExpenseGroupDto[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState<HomeExpenseGroupDto | null>(null);

  // Filtering and sorting state
  const [searchTerm, setSearchTerm] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [sortBy, setSortBy] = useState<keyof HomeExpenseGroupDto>('name');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc');
  
  // Pagination state
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  // Handlers
  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(event.target.value);
    setPage(0); // Reset to first page when searching
  };

  const handleTypeChange = (event: SelectChangeEvent<string>) => {
    setTypeFilter(event.target.value);
    setPage(0); // Reset to first page when filtering
  };

  const handlePageChange = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleRowsPerPageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const fetchGroups = async () => {
    setLoading(true);
    setError(null);
    try {
      setGroups(await getHomeExpenseGroups(expenseType));
    } catch (e: any) {
      setError(e.message || 'Falha ao carregar os grupos de despesas');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { 
    fetchGroups(); 
  }, [expenseType]);

  // Filtered and sorted data
  const filteredAndSortedGroups = useMemo(() => {
    let filtered = [...groups];

    // Apply search filter
    if (searchTerm) {
      filtered = filtered.filter(group => 
        group.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (group.description && group.description.toLowerCase().includes(searchTerm.toLowerCase()))
      );
    }

    // Apply type filter
    if (typeFilter === 'income') {
      filtered = filtered.filter(group => group.isIncomeGroup);
    } else if (typeFilter === 'expense') {
      filtered = filtered.filter(group => !group.isIncomeGroup);
    }

    // Apply sorting
    filtered.sort((a, b) => {
      const aValue = a[sortBy as keyof HomeExpenseGroupDto] || '';
      const bValue = b[sortBy as keyof HomeExpenseGroupDto] || '';
      
      if (typeof aValue === 'string' && typeof bValue === 'string') {
        const comparison = aValue.localeCompare(bValue);
        return sortOrder === 'asc' ? comparison : -comparison;
      }
      
      if (typeof aValue === 'boolean' && typeof bValue === 'boolean') {
        const comparison = Number(bValue) - Number(aValue);
        return sortOrder === 'asc' ? comparison : -comparison;
      }
      
      return 0;
    });

    return filtered;
  }, [groups, searchTerm, typeFilter, sortBy, sortOrder]);

  // Paginated data
  const paginatedGroups = useMemo(() => {
    const startIndex = page * rowsPerPage;
    return filteredAndSortedGroups.slice(startIndex, startIndex + rowsPerPage);
  }, [filteredAndSortedGroups, page, rowsPerPage]);

  // Total count for pagination
  const totalGroups = filteredAndSortedGroups.length;

  const handleSort = (property: keyof HomeExpenseGroupDto) => {
    const isAsc = sortBy === property && sortOrder === 'asc';
    setSortOrder(isAsc ? 'desc' : 'asc');
    setSortBy(property);
  };

  const clearFilters = () => {
    setSearchTerm('');
    setTypeFilter('');
    setPage(0);
  };

  const handleAdd = () => {
    setEditing(null);
    setShowForm(true);
  };

  const handleEdit = async (publicId: string) => {
    setLoading(true);
    try {
      const group = await getHomeExpenseGroupById(publicId);
      setEditing(group);
      setShowForm(true);
    } catch (e: any) {
      setError(e.message || 'Falha ao carregar os detalhes do grupo');
    } finally { 
      setLoading(false); 
    }
  };

  const handleDelete = async (publicId: string) => {
    if (!window.confirm('Tem a certeza de que pretende eliminar este grupo de despesas?')) return;
    setLoading(true);
    try {
      await deleteHomeExpenseGroup(publicId);
      fetchGroups();
    } catch (e: any) {
      setError(e.message || 'Falha ao eliminar o grupo de despesas');
    } finally { 
      setLoading(false); 
    }
  };

  const handleFormSubmit = async (data: {
    name: string;
    description?: string;
    isIncomeGroup: boolean;
    color?: string;
    categoryId?: string;
    isShoppingDefault: boolean;
  }) => {
    setLoading(true);
    try {
      console.log('[DEBUG HomeExpenseGroupList] handleFormSubmit data:', data);
      console.log('[DEBUG HomeExpenseGroupList] editing:', editing);
      
      if (editing && editing.publicId) {
        console.log('[DEBUG HomeExpenseGroupList] Calling updateHomeExpenseGroup with:', {
          publicId: editing.publicId,
          ...data
        });
        await updateHomeExpenseGroup(
          editing.publicId,
          data.name,
          data.description,
          data.isIncomeGroup,
          data.color,
          data.categoryId,
          data.isShoppingDefault
        );
      } else {
        console.log('[DEBUG HomeExpenseGroupList] Calling createHomeExpenseGroup with:', data);
        await createHomeExpenseGroup(
          data.name,
          data.description,
          data.isIncomeGroup,
          data.color,
          data.categoryId,
          expenseType,
          data.isShoppingDefault
        );
      }
      setShowForm(false);
      setEditing(null);
      fetchGroups();
    } catch (e: any) {
      setError(e.message || 'Falha ao guardar o grupo de despesas');
    } finally { 
      setLoading(false); 
    }
  };

  const handleCloseForm = () => {
    setShowForm(false);
    setEditing(null);
  };

  return (
    <Box>
      <PageHeader
        title="Grupos de despesas"
        actions={
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleAdd}
            disabled={loading}
          >
            Adicionar grupo
          </Button>
        }
      />

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Filtering Controls */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center' }}>
          <Box sx={{ flex: '1 1 300px', minWidth: '200px' }}>
            <TextField
              fullWidth
              size="small"
              label="Pesquisar grupos..."
              value={searchTerm}
              onChange={handleSearchChange}
              InputProps={{
                startAdornment: <SearchIcon color="action" sx={{ mr: 1 }} />
              }}
            />
          </Box>
          <Box sx={{ flex: '1 1 200px', minWidth: '150px' }}>
            <FormControl fullWidth size="small">
              <InputLabel>Tipo de grupo</InputLabel>
              <Select
                value={typeFilter}
                onChange={handleTypeChange}
                label="Tipo de grupo"
              >
                <MenuItem value="">Todos os grupos</MenuItem>
                <MenuItem value="income">Grupos de receitas</MenuItem>
                <MenuItem value="expense">Grupos de despesas</MenuItem>
              </Select>
            </FormControl>
          </Box>
          <Button
            variant="outlined"
            startIcon={<ClearIcon />}
            onClick={clearFilters}
            disabled={!searchTerm && !typeFilter}
          >
            Limpar
          </Button>
          <Typography variant="body2" color="text.secondary" sx={{ ml: 'auto' }}>
            A mostrar {paginatedGroups.length} de {filteredAndSortedGroups.length} grupos
          </Typography>
        </Box>
      </Paper>

      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>
                <TableSortLabel
                  active={sortBy === 'name'}
                  direction={sortBy === 'name' ? sortOrder : 'asc'}
                  onClick={() => handleSort('name')}
                >
                  Nome
                </TableSortLabel>
              </TableCell>
              <TableCell>Descrição</TableCell>
              <TableCell>Categoria</TableCell>
              <TableCell>
                <TableSortLabel
                  active={sortBy === 'isIncomeGroup'}
                  direction={sortBy === 'isIncomeGroup' ? sortOrder : 'asc'}
                  onClick={() => handleSort('isIncomeGroup')}
                >
                  Tipo
                </TableSortLabel>
              </TableCell>
              <TableCell>Cor</TableCell>
              <TableCell align="right">Ações</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {paginatedGroups.map((group) => (
              <TableRow key={group.publicId}>
                <TableCell>
                  <Typography variant="body2" fontWeight="medium">
                    {group.name}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body2" color="text.secondary">
                    {group.description || '-'}
                  </Typography>
                </TableCell>
                <TableCell>
                  {group.category ? (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      {group.category.color && (
                        <Box 
                          sx={{ 
                            width: 16, 
                            height: 16, 
                            borderRadius: '50%', 
                            backgroundColor: group.category.color,
                            border: '1px solid #ccc'
                          }} 
                        />
                      )}
                      <Typography variant="body2">
                        {group.category.name}
                      </Typography>
                    </Box>
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      -
                    </Typography>
                  )}
                </TableCell>
                <TableCell>
                  <Chip 
                    label={group.isIncomeGroup ? 'Receita' : 'Despesa'} 
                    color={group.isIncomeGroup ? 'success' : 'error'}
                    size="small"
                  />
                  {group.isShoppingDefault && (
                    <Chip label="Compras" color="primary" variant="outlined" size="small" sx={{ ml: 1 }} />
                  )}
                </TableCell>
                <TableCell>
                  {group.color && (
                    <Box 
                      sx={{ 
                        width: 24, 
                        height: 24, 
                        borderRadius: 1, 
                        backgroundColor: group.color,
                        border: '1px solid #ccc'
                      }} 
                    />
                  )}
                  {!group.color && (
                    <Typography variant="body2" color="text.secondary">
                      -
                    </Typography>
                  )}
                </TableCell>
                <TableCell align="right">
                  <IconButton 
                    size="small" 
                    onClick={() => handleEdit(group.publicId)}
                    disabled={loading}
                  >
                    <EditIcon fontSize="small" />
                  </IconButton>
                  <IconButton 
                    size="small" 
                    color="error" 
                    onClick={() => handleDelete(group.publicId)}
                    disabled={loading}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
            {paginatedGroups.length === 0 && !loading && (
              <TableRow>
                <TableCell colSpan={5} align="center">
                  <Typography variant="body2" color="text.secondary" py={4}>
                    Nenhum grupo de despesas encontrado. Clique em «Adicionar grupo» para criar o primeiro.
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Pagination */}
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
        <TablePagination
          component="div"
          count={totalGroups}
          page={page}
          onPageChange={handlePageChange}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleRowsPerPageChange}
          rowsPerPageOptions={[5, 10, 25, 50]}
          labelRowsPerPage="Linhas por página:"
          labelDisplayedRows={({ from, to, count }) => `${from}–${to} de ${count !== -1 ? count : `mais de ${to}`}`}
        />
      </Box>

      <Dialog open={showForm} onClose={handleCloseForm} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editing ? 'Editar grupo de despesas' : 'Adicionar novo grupo de despesas'}
        </DialogTitle>
        <DialogContent>
          <HomeExpenseGroupForm 
            initial={editing || undefined} 
            onSubmit={handleFormSubmit} 
            onCancel={handleCloseForm}
            loading={loading}
          />
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default HomeExpenseGroupList;
